// Armador de datos + descarga/preview del ACTA DE REPARTO desde la plantilla oficial
// .docx (con logos de cabecera/pie). Resuelve los nombres de los TRES firmantes del
// formato oficial (Director de Instrucción · Contratista/Funcionario asignado [Quien
// recibe] · Técnico administrativo [Elaboró]) y **incrusta la imagen de firma real** de
// cada uno (la que cargaron en «Mi firma») usando el módulo de imágenes de docxtemplater.
import type { Solicitud, Firmante } from '@/lib/preRadicacion';
import { usuarioActual } from '@/lib/instruccion';
import { descargarBlob } from '@/lib/api';
import { cargarPlantilla, MIME_DOCX, PlantillaFaltanteError } from './motor';
import { PLANTILLAS } from './registro';
import { fechaEnLetras } from './fechas';

// Encabezado oficial de la dependencia (texto de la plantilla F-ACTA_DE_REPARTO).
const ENCABEZADO =
  'DEPARTAMENTO DE RISARALDA, DIRECCIÓN ADMINISTRATIVA DE CONTROL DISCIPLINARIO INTERNO DE INSTRUCCIÓN';

const hoyISO = () => new Date().toISOString().slice(0, 10);

// PNG 1x1 transparente: se usa cuando un firmante no tiene firma cargada (línea en blanco).
const PNG_TRANSPARENTE = Uint8Array.from(atob(
  'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+M8AAAMBAQDJ/pLvAAAAAElFTkSuQmCC',
), (c) => c.charCodeAt(0));

/** Datos que rellenan la plantilla del acta de reparto (3 firmas del formato oficial). */
export function datosActaReparto(s: Solicitud, firmantes: Firmante[]) {
  const nombre = (id?: string | null) => firmantes.find((f) => f.id === id)?.nombre ?? '';
  const radicado = s.radicado_completo ?? s.radicado_origen ?? '—';
  // El Técnico administrativo (Elaboró) cae al usuario en sesión si no se fijó explícito.
  const tecnico = nombre(s.tecnico_id) || usuarioActual()?.nombre || '';

  return {
    ENCABEZADO,
    LUGAR: 'Pereira',
    FECHA_LETRAS: fechaEnLetras(hoyISO()),
    RADICADO: radicado,
    DIRECTOR: nombre(s.director_id) || '_____________________', // Director de Instrucción
    ABOGADO: nombre(s.abogado_id) || '_____________', // Contratista/Funcionario (Quien recibe)
    TECNICO: tecnico || '_____________', // Técnico administrativo (Elaboró)
    // Tabla ASUNTOS A REPARTIR. Por ahora una fila (el radicado); admite varias.
    ASUNTOS: [
      { RADICADO: radicado, IMPLICADO: '', TRAMITE: '', FOLIOS: '' },
    ],
  };
}

/** Descarga (proxy autenticado) la imagen de firma; si no hay, devuelve PNG transparente. */
async function firmaBytes(firmaKey?: string | null): Promise<Uint8Array> {
  if (!firmaKey) return PNG_TRANSPARENTE;
  try {
    const blob = await descargarBlob(`/documentos/descargar?path=${encodeURIComponent(firmaKey)}`);
    return new Uint8Array(await blob.arrayBuffer());
  } catch {
    return PNG_TRANSPARENTE; // firma no disponible → línea en blanco (no rompe el acta)
  }
}

/** Trae las 3 imágenes de firma (Director / Quien recibe / Elaboró) en paralelo,
 *  indexadas por el NOMBRE del tag (que es lo que se pasa como valor en los datos). */
async function firmasDeActa(s: Solicitud, firmantes: Firmante[]): Promise<Record<string, Uint8Array>> {
  const key = (id?: string | null) => firmantes.find((f) => f.id === id)?.firmaKey ?? null;
  const tecnicoKey = key(s.tecnico_id) ?? key(usuarioActual()?.id);
  const [d, a, t] = await Promise.all([
    firmaBytes(key(s.director_id)),
    firmaBytes(key(s.abogado_id)),
    firmaBytes(tecnicoKey),
  ]);
  return { FIRMA_DIRECTOR: d, FIRMA_ABOGADO: a, FIRMA_TECNICO: t };
}

/**
 * Renderiza el acta con el módulo de imágenes (docxtemplater-image-module-free), que
 * incrusta la imagen de firma real de cada firmante sobre su línea. Devuelve el zip
 * docxtemplater ya renderizado (para blob/descarga).
 *
 * OJO: el valor de un tag de imagen debe ser un STRING (el módulo trata un valor-objeto
 * como resultado ya resuelto y rompe). Pasamos el nombre del tag como valor y `getImage`
 * lo mapea a los bytes reales.
 */
async function renderActa(s: Solicitud, firmantes: Firmante[]) {
  const ruta = PLANTILLAS.ACTA_REPARTO.ruta;
  const [contenido, bytesPorTag] = await Promise.all([
    cargarPlantilla(ruta),
    firmasDeActa(s, firmantes),
  ]);
  const [{ default: PizZip }, { default: Docxtemplater }, { default: ImageModule }] = await Promise.all([
    import('pizzip'),
    import('docxtemplater'),
    import('docxtemplater-image-module-free'),
  ]);
  const imageModule = new ImageModule({
    getImage: (tagValue: string) => bytesPorTag[tagValue] ?? PNG_TRANSPARENTE,
    getSize: () => [150, 45] as [number, number],
    centered: false,
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const doc = new Docxtemplater(new PizZip(contenido), {
    modules: [imageModule as any],
    paragraphLoop: true,
    linebreaks: true,
    delimiters: { start: '{', end: '}' },
    nullGetter: () => '',
  });
  doc.render({
    ...datosActaReparto(s, firmantes),
    // Los valores de los tags de imagen son STRINGS (el nombre del tag).
    FIRMA_DIRECTOR: 'FIRMA_DIRECTOR',
    FIRMA_ABOGADO: 'FIRMA_ABOGADO',
    FIRMA_TECNICO: 'FIRMA_TECNICO',
  });
  return doc.getZip();
}

/** Genera el .docx del acta (con firmas-imagen) como Blob para previsualizar. */
export async function blobActaReparto(s: Solicitud, firmantes: Firmante[]): Promise<Blob> {
  const zip = await renderActa(s, firmantes);
  return zip.generate({ type: 'blob', mimeType: MIME_DOCX }) as Blob;
}

/**
 * Descarga el Acta de Reparto en Word (con logos + firmas-imagen).
 * @throws PlantillaFaltanteError si la plantilla no está cargada en assets.
 */
export async function descargarActaReparto(s: Solicitud, firmantes: Firmante[]): Promise<void> {
  const zip = await renderActa(s, firmantes);
  const blob = zip.generate({ type: 'blob', mimeType: MIME_DOCX }) as Blob;
  const { saveAs } = await import('file-saver');
  saveAs(blob, `acta-reparto-${s.radicado_completo ?? s.id}.docx`);
}

// Re-export por si algún consumidor lo necesita para el fallback.
export { PlantillaFaltanteError };
